/*!
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */

import React, { useEffect, useState, ReactElement } from "react";

import { connect, useDispatch } from "react-redux";
import { withRouter } from "react-router-dom";
import { bindActionCreators, Dispatch } from "redux";

import { SearchBox, Stack } from "office-ui-fabric-react/lib";

import { PrimaryButton } from "@fluentui/react";

import * as UserProfileActions from "Dataflows/UserProfile/UserProfileAction";
import * as CommonDataActions from "Dataflows/CommonData/CommonDataAction";

import ListCounts from "Components/Common/ListCounts/ListCounts";
import { Title } from "Components/Common/Results/Result.styles";
import StudentList from "Components/StudentList/StudentList";
import UpdateSARegistrationStatus from "Components/UpdateSARegistrationStatus/UpdateSARegistrationStatus";
import {
  defaultListingPageSize,
  defaultMlsaFilterValues,
  mlsaUserSidebarFilterFields,
} from "Constants/ProfileConstants";
import { AppState } from "Dataflows/Reducers";
import {
  getCurrentPage,
  getFilteredCount,
  getPageSize,
  getSearchText,
  getUserProfiles,
  getUserProfilesFilterValues,
  isUserProfilesLoading,
} from "Dataflows/UserProfile/UserProfileSelector";
import { IFilterValue } from "Interfaces/IFilterValue";

import {
  buttonStackTokens,
  containerStackStyles,
  filterButtonStyles,
  listStackStyles,
  searchBoxStyles,
  tableStackStyles,
  PaddedFlexRow,
} from "../Common/CommonContainer.styles";
import SideBarContainer from "../SideBarContainer/SideBarContainer";
import { IStudentsContainerProps } from "./IStudentsContainerProps";

import { TenantTypesEnum } from "Constants/TenantTypes";

/**
 * Students Container element that displays side bar filtering and result listing.
 * @FunctionComponent
 * @param {IStudentsContainerProps} props The side bar props.
 * @returns {React.FunctionComponent<IStudentsContainerProps>} The side bar component.
 */
const StudentsContainer: React.FunctionComponent<IStudentsContainerProps> = (
  props: IStudentsContainerProps
): ReactElement => {
  const {
    selectedUserProfilesFilterValues,
    userProfiles,
    isUserProfilesLoading,
    currentPage,
    pageSize,
    filteredCount,
    actions: {
      addUserProfilesFilterValue,
      removeUserProfilesFilterValue,
      removeUserProfilesProvinceFilterValue,
      searchUserProfiles,
      updateUserProfilesFilterValue,
      resetUserProfileFilterValue,
    },
  } = props;

  const dispatch = useDispatch();

  const [searchText, setSearchText] = useState(props.searchText);

  /**
   * Call to reset filter values.
   * @method
   */
  const resetFilterValues = (): void => {
    resetUserProfileFilterValue();

    setSearchText("");
    searchUserProfiles("", defaultMlsaFilterValues, 1, defaultListingPageSize);
  };

  useEffect(() => {
    searchUserProfiles(searchText, selectedUserProfilesFilterValues, currentPage, pageSize);
  }, [currentPage, pageSize]);

  useEffect(() => {
    dispatch(CommonDataActions.loadCommonData());
  }, []);

  /**
   * Call search function for entered text.
   * @method
   * @param {string} searchValue The search text.
   * @param {IFilterValue[]} selectedUserProfilesFilterValues The filter values.
   * @param {number} page The fpage number.
   * @returns {void}
   */
  const handleSearch = (
    searchValue: string,
    selectedUserProfilesFilterValues: IFilterValue[],
    page: number,
    pageSize: number
  ): void => {
    searchUserProfiles(searchValue, selectedUserProfilesFilterValues, page, pageSize);
  };

  const onClear = (_: any): void => {
    searchUserProfiles("", selectedUserProfilesFilterValues, 1, pageSize);
  };

  return (
    <Stack horizontal styles={containerStackStyles}>
      <Stack.Item disableShrink>
        <SideBarContainer
          addFilterValue={addUserProfilesFilterValue}
          removeFilterValue={removeUserProfilesFilterValue}
          updateFilterValue={updateUserProfilesFilterValue}
          removeFilterProvinceValue={removeUserProfilesProvinceFilterValue}
          selectedFilterValues={selectedUserProfilesFilterValues}
          filterFields={mlsaUserSidebarFilterFields}
          tenant={TenantTypesEnum.MSP}
        />
        <Stack horizontal tokens={buttonStackTokens}>
          <PrimaryButton
            styles={filterButtonStyles()}
            text="Apply"
            onClick={(): void =>
              handleSearch(searchText, selectedUserProfilesFilterValues, 1, pageSize)
            }
          />
          <PrimaryButton styles={filterButtonStyles()} text="Reset" onClick={resetFilterValues} />
        </Stack>
      </Stack.Item>
      <Stack.Item grow styles={listStackStyles()}>
        <Stack styles={tableStackStyles}>
          <PaddedFlexRow>
            <Title>Students</Title>
            <ListCounts total={filteredCount} loading={isUserProfilesLoading} />
          </PaddedFlexRow>
          <SearchBox
            placeholder="Search students"
            value={searchText}
            onSearch={(searchValue: string): void =>
              handleSearch(searchValue, selectedUserProfilesFilterValues, 1, pageSize)
            }
            onChange={(event?: React.ChangeEvent<HTMLInputElement>, newValue?: string): void =>
              setSearchText(newValue || "")
            }
            onClear={onClear}
            styles={searchBoxStyles}
          />
          <UpdateSARegistrationStatus />
          <StudentList data={userProfiles} filteredCount={filteredCount} pageSize={pageSize} />
        </Stack>
      </Stack.Item>
    </Stack>
  );
};

/**
 * Maps StudentsContainer to redux state
 * @param {AppState} state The App state.
 */
const mapStateToProps = (state: AppState) => ({
  userProfiles: getUserProfiles(state.userProfileState),
  isUserProfilesLoading: isUserProfilesLoading(state.userProfileState),
  currentPage: getCurrentPage(state.userProfileState),
  pageSize: getPageSize(state.userProfileState),
  searchText: getSearchText(state.userProfileState),
  selectedUserProfilesFilterValues: getUserProfilesFilterValues(state.userProfileState),
  filteredCount: getFilteredCount(state.userProfileState),
});

/**
 * Maps Component's dispatch/handler props to redux state
 * @param {Dispatch} dispatch The dispatch.
 */
const mapDispatchToProps = (dispatch: Dispatch<UserProfileActions.UserProfileActionTypes>) => ({
  actions: {
    loadCommonData: bindActionCreators(CommonDataActions.loadCommonData, dispatch),
    searchUserProfiles: bindActionCreators(UserProfileActions.searchUserProfiles, dispatch),
    addUserProfilesFilterValue: bindActionCreators(
      UserProfileActions.addUserProfilesFilterValue,
      dispatch
    ),
    removeUserProfilesFilterValue: bindActionCreators(
      UserProfileActions.removeUserProfilesFilterValue,
      dispatch
    ),
    removeUserProfilesProvinceFilterValue: bindActionCreators(
      UserProfileActions.removeUserProfilesFilterProvinceValue,
      dispatch
    ),
    updateUserProfilesFilterValue: bindActionCreators(
      UserProfileActions.updateUserProfilesFilterValue,
      dispatch
    ),
    resetUserProfileFilterValue: bindActionCreators(
      UserProfileActions.resetUserProfileFilterValue,
      dispatch
    ),
  },
});

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(StudentsContainer));
